import CheckCircleOutlineIcon from '@atlaskit/icon/glyph/check-circle-outline';
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import { B400, N0, N50, N800 } from '@atlaskit/theme/colors';
import { MarieSchemaAnalytics } from '@atlassiansox/bxpkit-core';
import styled from '@emotion/styled';
import { debounce } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSiteNameValidation } from '../../../../../hook';
import {
  getTranslatableError,
  messages
} from '../../../../../messages/sign-up-form';
import {
  AnalyticsTrackType,
  Sites
} from '../../../../../model/wac-signup-form';
import { TooltipIcon } from '../Icons/tooltip-icon';
import {
  CompoundField,
  ErrorMessageWithDefaultClassName,
  FieldContainer,
  InfoIcon,
  InputDescription,
  Label,
  SiteNameAfter,
  SiteNameAfterLabel,
  SitePickerToggle,
  SuccessTextfieldWrapper,
  UnderLabelMessage,
  InputSiteNameInstructions
} from './styled';

export type SiteNameProps = {
  readonly onSiteNameChange: (siteName: string, siteNameLength: number) => void;
  readonly onSiteNameUseExisting: () => void;
  readonly sites: Sites[] | null;
  readonly siteNameAfter: string;
  readonly wacEnvUrl: string;
  readonly analyticsTrack: AnalyticsTrackType;
  readonly shouldShowUseExisting: boolean;
  readonly setIsValidSiteName: (val: boolean) => void;
  readonly isDisabled: boolean;
  readonly fadeOnDisable?: boolean;
  readonly outlineOnFocus?: boolean;
  readonly popOverError?: boolean;
  readonly showWarningIcon?: boolean;
  readonly isSuiteSignup?: boolean;
  readonly showInfoIcon?: boolean;
  readonly isDeferredSiteNameEnabled?: boolean;
  setExposedToDeferSitename: (exposed: boolean) => void;
  readonly activateProductError?: boolean;
  readonly setActivateProductError?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  readonly recommendedSiteName?: string;
  readonly siteNameGenerationExperiment?: boolean;
  readonly siteNameAddtionalCopyExperiment?: boolean;
};

export function useDocumentClick(
  value: boolean,
  callback: React.Dispatch<React.SetStateAction<boolean>>,
  ignoreClass: string
) {
  useEffect(() => {
    const handleClick = (event: any) => {
      let isIgnored = false;
      if (event && event.target && event.target.classList) {
        isIgnored = event.target.classList.contains(ignoreClass);
      }

      if (value && !isIgnored) {
        callback(!value);
      }
    };
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [value, callback, ignoreClass]);
}

interface ToolTipProps {
  readonly isDisabled?: boolean;
  readonly fadeOnDisable?: boolean;
}

export const DomainToolTip = styled.div<ToolTipProps>`
  cursor: ${(props) =>
    props.isDisabled && props.fadeOnDisable ? 'not-allowed' : 'pointer'};
  margin: 0 3px;
  border-radius: 3px;
  svg {
    height: 14px;
    width: 14px;
  }
`;
export const ToolTipText = styled.div<ToolTipProps>`
  display: ${(props) =>
    props.isDisabled && props.fadeOnDisable ? 'none' : 'block'};
  background: ${N0};
  border: 1px solid ${B400};
  border-radius: 2px;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.25);
  color: ${N800};
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.42857143;
  padding: 12px;
  z-index: 1;
  position: absolute;
  left: 6px;
  top: 25px;
  max-width: 298px;
`;

export const ToolTipContainer = styled.div`
  position: relative;
  z-index: 11;
  display: none;
  @supports (display: grid) {
    display: flex;
    align-items: center;
  }
`;

const sendErrorEvent = debounce((segment, message, isValidating) => {
  if (isValidating && message) {
    sendErrorEvent(segment, message, isValidating);
  } else if (message) {
    segment('bxp.formCloudSignup.siteValidationError', { message });
  }
}, 3000);

export function SiteNameField({
  onSiteNameChange,
  onSiteNameUseExisting,
  sites,
  siteNameAfter,
  shouldShowUseExisting,
  setIsValidSiteName,
  analyticsTrack,
  isDisabled,
  fadeOnDisable,
  outlineOnFocus,
  popOverError,
  showWarningIcon,
  isSuiteSignup,
  isDeferredSiteNameEnabled,
  setExposedToDeferSitename,
  activateProductError,
  setActivateProductError,
  recommendedSiteName,
  siteNameGenerationExperiment,
  siteNameAddtionalCopyExperiment
}: SiteNameProps) {
  useEffect(() => {
    setExposedToDeferSitename(true);
  }, []);

  const checkIsDisabled = () => {
    // ensure isDisabled can be set when form is submitting,
    // otherwise check the field progress state
    return isDisabled;
  };
  const { segment } = analyticsTrack;

  const labelMessage = useMemo(() => {
    if (isSuiteSignup) {
      return messages.siteNameSuite;
    } else if (isDeferredSiteNameEnabled) {
      return messages.siteNameOptional;
    } else {
      return messages.yourSite;
    }
  }, [isSuiteSignup, isDeferredSiteNameEnabled]);

  const [siteName, setSiteName] = useState(
    sites !== null && sites[0] ? sites[0].displayName : ''
  );
  const [toolTipOpen, setToolTipOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [fieldDisabled, setFieldDisabled] = useState(checkIsDisabled());
  const { isValid, message, isValidating } = useSiteNameValidation(
    siteName,
    analyticsTrack,
    isDeferredSiteNameEnabled,
    activateProductError
  );
  const toolTipTrigger = 'domain-name-tooltip-trigger';
  const inputFieldId = 'site-name-field-input';
  const siteNameExperiment =
    siteNameGenerationExperiment && siteNameAddtionalCopyExperiment;
  const siteNameInstructions = siteNameGenerationExperiment
    ? messages.siteNameInstructionsExperimentB
    : messages.siteNameInstructions;

  const hideErrorMessageSiteNameExperiment =
    message ===
      'Your site name must be at least 3 characters long. Use only lowercase letters and numbers.' &&
    siteNameAddtionalCopyExperiment;

  useDocumentClick(toolTipOpen, setToolTipOpen, toolTipTrigger);
  const { setSuiteName } = useContext(MarieSchemaAnalytics);

  useEffect(() => {
    if (recommendedSiteName) {
      setSiteName(recommendedSiteName);
    }
  }, [recommendedSiteName]);

  useEffect(() => {
    isValid
      ? onSiteNameChange(siteName, siteName.length)
      : onSiteNameChange('', siteName.length);
    setIsValidSiteName(isValid);

    if (!isValid) {
      sendErrorEvent(segment, message, isValidating);
    } else {
      setSuiteName(siteName);
      sendErrorEvent.cancel();
    }

    setFieldDisabled(checkIsDisabled());
  }, [siteName, isValid, message, isDisabled]);

  return (
    <FieldContainer className="site-name-field sign-up-field-container">
      <CompoundField isDisabled={fieldDisabled} fadeOnDisable={fadeOnDisable}>
        <Label htmlFor={inputFieldId}>
          <FormattedMessage {...labelMessage} />
          {isSuiteSignup && (
            <InfoIcon>
              <EditorPanelIcon label="infoIcon" size="medium" />
              <div>
                <ToolTipText
                  className={`${toolTipTrigger}-devops`}
                  isDisabled={fieldDisabled}
                  fadeOnDisable={fadeOnDisable}
                >
                  <FormattedMessage {...messages.helpTooltipTextDevops} />
                </ToolTipText>
              </div>
            </InfoIcon>
          )}
        </Label>
        {!siteNameExperiment && (
          <ToolTipContainer>
            <DomainToolTip
              isDisabled={fieldDisabled}
              fadeOnDisable={fadeOnDisable}
              data-testid="domain-tool-tip"
              className={toolTipOpen ? 'selected' : ''}
              onClick={() => {
                if (fieldDisabled) {
                  return;
                }
                if (!toolTipOpen) {
                  segment('bxp.formCloudSignup.toolTipDisplayed', {});
                }
                setToolTipOpen(!toolTipOpen);
              }}
            >
              {!isSuiteSignup && (
                <TooltipIcon
                  fillColor={toolTipOpen && !fieldDisabled ? B400 : N50}
                />
              )}
            </DomainToolTip>
            {toolTipOpen ? (
              <ToolTipText
                className={toolTipTrigger}
                isDisabled={fieldDisabled}
                fadeOnDisable={fadeOnDisable}
              >
                {isDeferredSiteNameEnabled ? (
                  <FormattedMessage {...messages.deferredSiteNameTooltipText} />
                ) : (
                  <FormattedMessage {...messages.helpTooltipText} />
                )}
              </ToolTipText>
            ) : null}
          </ToolTipContainer>
        )}
      </CompoundField>
      {isDeferredSiteNameEnabled && (
        <UnderLabelMessage>
          <FormattedMessage {...messages.siteNameOptionalMessageN2N} />
        </UnderLabelMessage>
      )}
      <SuccessTextfieldWrapper
        isValidating={isValidating}
        isValid={!!isValid}
        textInput={siteName}
        isEmpty={siteName.length === 0}
        isFocused={isFocused}
        outlineOnFocus={outlineOnFocus}
        isDisabled={fieldDisabled}
        fadeOnDisable={fadeOnDisable}
      >
        <Textfield
          className="site-name-field input"
          id={inputFieldId}
          data-testid="site-name-field"
          isRequired={!isDeferredSiteNameEnabled}
          isDisabled={fieldDisabled}
          value={siteName}
          isInvalid={isValid === false}
          autoCorrect="off"
          autoCapitalize="none"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={{ height: 'inherit', width: '100%' }}
          onChange={(event: any) => {
            setSiteName(event.currentTarget.value);
            if (setActivateProductError) setActivateProductError(false);
          }}
          elemAfterInput={
            <SiteNameAfter>
              <SiteNameAfterLabel>{siteNameAfter}</SiteNameAfterLabel>
              {isValid && siteName !== '' && !isValidating ? (
                <CheckCircleOutlineIcon
                  label="success"
                  primaryColor="#36B37E"
                />
              ) : isValid === false && siteName !== '' && !isValidating ? (
                showWarningIcon && (
                  <WarningIcon label="warning" primaryColor="#DE350B" />
                )
              ) : isValidating ? (
                <Spinner size="small" />
              ) : null}
            </SiteNameAfter>
          }
        />
        {message && !hideErrorMessageSiteNameExperiment && (
          <ErrorMessageWithDefaultClassName
            isDisabled={fieldDisabled}
            fadeOnDisable={fadeOnDisable}
            popOverError={popOverError}
          >
            <FormattedMessage
              {...getTranslatableError(
                message,
                isSuiteSignup,
                isDeferredSiteNameEnabled
              )}
            />
          </ErrorMessageWithDefaultClassName>
        )}
      </SuccessTextfieldWrapper>

      {isSuiteSignup && (
        <InputDescription>
          <FormattedMessage {...messages.siteNameSuiteDescriptionDevops} />
        </InputDescription>
      )}

      {shouldShowUseExisting && (
        <SitePickerToggle
          onClick={() => {
            if (!fieldDisabled) {
              onSiteNameUseExisting();
            }
          }}
        >
          <FormattedMessage {...messages.existingSite} />
        </SitePickerToggle>
      )}
      {siteNameExperiment && (
        <InputSiteNameInstructions>
          <FormattedMessage {...siteNameInstructions} />
        </InputSiteNameInstructions>
      )}
    </FieldContainer>
  );
}
